import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Layout from '../components/Layout';
import { THEMES, formatDateTime, themeLabel } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || '';

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Ops({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('access_token');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/ops/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // atualiza sozinho a cada minuto
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && !stats) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <p className="text-sm text-slate-400">Carregando…</p>
      </Layout>
    );
  }

  // Gráfico quer ordem cronológica; a API devolve mais recente primeiro.
  const collectionSeries = [...(stats.collection_runs || [])].reverse().map((r, i) => ({
    idx: i + 1, fetched: r.fetched, new: r.new_articles, pending: r.pending_after,
  }));

  const termsByTheme = {};
  for (const t of stats.search_terms || []) {
    termsByTheme[t.theme] = (termsByTheme[t.theme] || 0) + t.count;
  }

  const lastClassification = stats.classification_runs?.[0];
  const lastCollection = stats.collection_runs?.[0];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Monitoramento</h1>
          <p className="text-sm text-slate-500">Status da pesquisa, coleta, classificação e custo — atualiza sozinho a cada minuto.</p>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Notícias na base" value={stats.totals.news} />
          <StatCard
            label="Pendentes"
            value={stats.totals.pending}
            hint={stats.totals.pending > 0 ? 'aguardando classificação' : 'tudo em dia'}
          />
          <StatCard label="Leads extraídos" value={stats.totals.leads} />
          <StatCard label="Posts cadastrados" value={stats.totals.posts} />
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Termos de busca ativos" value={stats.search_terms_total} hint="4 temas × 3 idiomas" />
          <StatCard label="Classificadas (total)" value={stats.classified_total} />
          <StatCard label="Custo estimado (total)" value={`US$ ${stats.cost_total_usd.toFixed(2)}`} hint="dentro do plano Max" />
          <StatCard
            label="Última coleta"
            value={lastCollection ? formatDateTime(lastCollection.ran_at) : '—'}
            hint={lastCollection ? `+${lastCollection.new_articles} novas` : 'sem registro ainda'}
          />
        </div>

        <SectionCard title="Requisições de coleta (mais recentes primeiro)" subtitle="Buscadas / novas / pendentes acumuladas após cada rodada">
          {collectionSeries.length === 0 ? (
            <p className="text-sm text-slate-400">Nenhuma rodada registrada ainda.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={collectionSeries} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="newFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="idx" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v, name) => [v, name === 'new' ? 'Novas' : name === 'fetched' ? 'Buscadas' : 'Pendentes']} />
                <Area type="monotone" dataKey="new" stroke="#4f46e5" strokeWidth={2} fill="url(#newFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SectionCard title="Rodadas de classificação" subtitle="Feitas por esta sessão do Claude Code (plano Max, sem API paga)">
            {(!stats.classification_runs || stats.classification_runs.length === 0) ? (
              <p className="text-sm text-slate-400">Nenhuma rodada registrada ainda.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-400">
                      <th className="pb-2">Quando</th>
                      <th className="pb-2">Classificadas</th>
                      <th className="pb-2">Tokens (saída)</th>
                      <th className="pb-2">Custo est.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {stats.classification_runs.map((r, i) => (
                      <tr key={i} className={r.had_error ? 'bg-rose-50' : ''}>
                        <td className="py-1.5 text-slate-600">{formatDateTime(r.ran_at)}</td>
                        <td className="py-1.5">{r.classified}</td>
                        <td className="py-1.5 text-slate-500">{r.output_tokens.toLocaleString('pt-BR')}</td>
                        <td className="py-1.5 text-slate-500">US$ {r.cost_usd.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {lastClassification?.had_error && (
              <p className="mt-2 text-xs text-rose-500">A última rodada teve erro — confira o log do cron no jarvis.</p>
            )}
          </SectionCard>

          <SectionCard title="Termos de busca por tema" subtitle="Quanto mais termos, mais chance de achar notícia nova a cada ciclo">
            <div className="flex flex-col gap-2">
              {THEMES.map((t) => (
                <div key={t.value} className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-slate-600">
                    <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                    {themeLabel(t.value)}
                  </span>
                  <span className="text-sm font-semibold text-slate-800">{termsByTheme[t.value] || 0} termos</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </Layout>
  );
}

export default Ops;
